import React, { useRef, useState } from 'react';
import { Download, Copy, Check, MapPin, Phone, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

/**
 * Regional ad — Taranaki. Same brand & format as the other ads.
 *
 * Two formats:
 *   1) Portrait:  1080 × 1350
 *   2) Landscape: 1200 × 630
 */
const TaranakiRegionAd = () => {
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0f2818',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `TARANAKI FARMERS — GET YOUR NEXT JOB SORTED ✔️

Short on time or labour? Don't put it off.

Measured Livestock is in your region delivering:
•  Weighing
•  DNA Sampling
•  Herd Reads

Powered by the Arvo app — fast, simple on-farm capture with everything recorded and traceable. Available to download online.

Every job is Animal ID Verified, so you know it's done right.

📍 Taranaki Region
📞 Vaughan Keeghan — 027 253 6515

Book now or get in touch to line up your next job.
👉 measuredlivestock.com

#Taranaki #NZFarming #DairyNZ #BeefNZ #MeasuredLivestock`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const heroImg = 'https://images.unsplash.com/photo-1709733971576-8caa9df3abf9?w=1600&q=80';

  const servicePill = (label) => (
    <div style={{
      background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)',
      padding: '14px 18px', borderRadius: 10, color: 'white',
      fontSize: 20, fontWeight: 700, textAlign: 'center', letterSpacing: 0.3,
    }}>{label}</div>
  );

  return (
    <section className="bg-gradient-to-br from-[#0f2818] via-[#1a4d2e] to-[#0f2818] py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            New · Regional Ad · Taranaki
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <MapPin className="w-9 h-9 text-[#ff6b00]" />
            Taranaki — Get Your Next Job Sorted
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Regional creative featuring Vaughan Keeghan's contact — for the Taranaki market.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* === PORTRAIT 1080 × 1350 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-taranaki-1080x1350.png')}
                disabled={downloading === 'measuredlivestock-taranaki-1080x1350.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-taranaki-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-taranaki-1080x1350.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080,
                  height: 1350,
                  marginRight: -1080 * 0.58,
                  marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080,
                    height: 1350,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(180deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.86) 38%, rgba(15,40,24,0.96) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  {/* Brand corner */}
                  <div style={{
                    position: 'absolute', top: 38, left: 56, display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                      Measured Livestock
                    </span>
                  </div>

                  {/* Region pill */}
                  <div style={{
                    position: 'absolute', top: 32, right: 56,
                    background: '#ff6b00', color: 'white', padding: '10px 22px', borderRadius: 999,
                    fontSize: 13, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ fontSize: 15 }}>📍</span> Taranaki Region
                  </div>

                  {/* Headline */}
                  <div style={{ position: 'absolute', top: 130, left: 56, right: 56, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.85, letterSpacing: 6, textTransform: 'uppercase', marginBottom: 18 }}>
                      Taranaki Farmers
                    </div>
                    <h1 style={{
                      fontSize: 92, lineHeight: 1.0, fontWeight: 800, margin: 0,
                      letterSpacing: -2, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      Get Your Next
                    </h1>
                    <h1 style={{
                      fontSize: 92, lineHeight: 1.0, fontWeight: 800, margin: '4px 0 20px',
                      color: '#ff8533', letterSpacing: -2,
                    }}>
                      Job Sorted ✔
                    </h1>
                    <div style={{ fontSize: 26, opacity: 0.94, fontWeight: 500, lineHeight: 1.4, maxWidth: 820, margin: '0 auto' }}>
                      Short on time or labour? Don't put it off. We're in your region and ready to help.
                    </div>
                  </div>

                  {/* Services */}
                  <div style={{
                    position: 'absolute', top: 650, left: 56, right: 56,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14, textAlign: 'center',
                    }}>
                      What we deliver on-farm
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      {servicePill('Weighing')}
                      {servicePill('DNA Sampling')}
                      {servicePill('Herd Reads')}
                    </div>
                    <div style={{
                      marginTop: 18, background: 'rgba(255,255,255,0.04)',
                      border: '1.5px solid rgba(255,107,0,0.5)', borderRadius: 12,
                      padding: '16px 20px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 17, opacity: 0.95, lineHeight: 1.4 }}>
                        Powered by the <strong style={{ color: '#ff8533' }}>Arvo app</strong> — fast, simple
                        on-farm capture with everything recorded and traceable.
                      </div>
                    </div>
                    <div style={{
                      marginTop: 14, textAlign: 'center', fontSize: 17, fontWeight: 600, letterSpacing: 0.5,
                    }}>
                      ✔  Every job is <strong style={{ color: '#ff8533' }}>Animal ID Verified</strong>
                    </div>
                  </div>

                  {/* Contact block */}
                  <div style={{
                    position: 'absolute', bottom: 200, left: 56, right: 56,
                    background: 'rgba(255,107,0,0.14)', border: '2px solid #ff6b00',
                    borderRadius: 14, padding: '18px 22px',
                    display: 'flex', alignItems: 'center', gap: 14,
                  }}>
                    <div style={{
                      background: '#ff6b00', width: 54, height: 54, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 26 }}>📞</span>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#ff8533', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 2 }}>
                        Your local contact
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>Vaughan Keeghan</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#ff8533', letterSpacing: 1, marginTop: 2 }}>
                        027 253 6515
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{
                    position: 'absolute', bottom: 56, left: 56, right: 56, textAlign: 'center',
                  }}>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '20px 0', borderRadius: 10,
                      fontSize: 28, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      Book Now — measuredlivestock.com
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === LANDSCAPE 1200 × 630 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-taranaki-1200x630.png')}
                disabled={downloading === 'measuredlivestock-taranaki-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-taranaki-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-taranaki-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: 1200,
                  height: 630,
                  marginRight: -1200 * 0.45,
                  marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(110deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.90) 50%, rgba(15,40,24,0.55) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  <div style={{ position: 'absolute', top: 36, left: 56, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 6, height: 32, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, textTransform: 'uppercase', opacity: 0.92 }}>
                      Measured Livestock
                    </span>
                  </div>

                  <div style={{
                    position: 'absolute', top: 36, right: 56,
                    background: '#ff6b00', color: 'white', padding: '8px 20px', borderRadius: 999,
                    fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
                  }}>
                    📍 Taranaki Region
                  </div>

                  {/* Left text */}
                  <div style={{ position: 'absolute', top: 110, left: 56, right: 480 }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.88, marginBottom: 6, letterSpacing: 4, textTransform: 'uppercase' }}>
                      Taranaki Farmers
                    </div>
                    <h1 style={{
                      fontSize: 62, lineHeight: 1.0, fontWeight: 800, margin: 0,
                      letterSpacing: -1.4,
                    }}>
                      Get Your Next
                    </h1>
                    <h1 style={{
                      fontSize: 62, lineHeight: 1.0, fontWeight: 800, margin: '4px 0 14px',
                      color: '#ff8533', letterSpacing: -1.4,
                    }}>
                      Job Sorted ✔
                    </h1>
                    <div style={{ fontSize: 17, fontWeight: 400, opacity: 0.94, lineHeight: 1.5, maxWidth: 620 }}>
                      Short on time or labour? Weighing · DNA Sampling · Herd Reads — every job
                      Animal ID Verified, powered by the <strong style={{ color: '#ff8533' }}>Arvo app</strong>.
                    </div>
                  </div>

                  {/* Right — contact card */}
                  <div style={{
                    position: 'absolute', top: 110, right: 56, width: 400,
                    background: 'rgba(255,107,0,0.14)', border: '2px solid #ff6b00',
                    borderRadius: 14, padding: '20px 22px',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#ff8533', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>
                      Your local contact
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>Vaughan Keeghan</div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: '#ff8533', letterSpacing: 1, marginTop: 6 }}>
                      027 253 6515
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12, opacity: 0.9, letterSpacing: 2, textTransform: 'uppercase' }}>
                      📞 Call to line up your next job
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{
                    position: 'absolute', bottom: 36, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                        Book online in 2 minutes
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 800 }}>measuredlivestock.com</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '18px 32px', borderRadius: 8,
                      fontSize: 20, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.4)',
                    }}>
                      Book Now →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <Phone className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-taranaki-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Targeting tip:</strong> Facebook Ads location: <strong>Taranaki Region + 20 km radius</strong>.
            Interests: <em>Dairy farming, Beef cattle, Livestock, Federated Farmers, NAIT</em>.
            Run as a <strong>Traffic / Messenger campaign</strong> — clicks go to measuredlivestock.com or DM Vaughan.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TaranakiRegionAd;
